import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Converte a VAPID public key (base64url) pro formato Uint8Array exigido pelo PushManager.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function isSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
}

// Garante que o service worker está registrado (index.js só registra em produção),
// e aguarda ele ficar pronto antes de mexer no pushManager.
async function getReadyRegistration() {
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register('/service-worker.js');
  }
  return navigator.serviceWorker.ready.then(() => reg);
}

// Hook de notificações push do PWA.
// basePath: '/push' (organizador) ou '/push/player' (comunidade)
// status: 'granted' | 'denied' | 'default' | 'unsupported'
export default function usePushNotifications({ basePath = '/push' } = {}) {
  const [status, setStatus] = useState(() => (isSupported() ? Notification.permission : 'unsupported'));
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshSubscribedState = useCallback(async () => {
    if (!isSupported()) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => {
    if (!isSupported()) {
      setStatus('unsupported');
      return;
    }
    setStatus(Notification.permission);
    refreshSubscribedState();
  }, [refreshSubscribedState]);

  const subscribe = useCallback(async () => {
    if (!isSupported()) return { ok: false, reason: 'unsupported' };
    setLoading(true);
    try {
      const { data: config } = await api.get('/push/config');
      if (!config.enabled || !config.public_key) return { ok: false, reason: 'not_configured' };

      const permission = await Notification.requestPermission();
      setStatus(permission);
      if (permission !== 'granted') return { ok: false, reason: 'permission_denied' };

      const reg = await getReadyRegistration();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.public_key),
        });
      }

      await api.post(`${basePath}/subscribe`, {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent,
      });

      setSubscribed(true);
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: 'unknown', message: err.message };
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported()) return { ok: false };
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.post(`${basePath}/unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      return { ok: true };
    } catch (err) {
      console.error('usePushNotifications.unsubscribe:', err);
      return { ok: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  const test = useCallback(async () => {
    try {
      const { data } = await api.post(`${basePath}/test`);
      return { ok: true, data };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message || err.message };
    }
  }, []);

  return { status, subscribed, loading, subscribe, unsubscribe, test };
}
